<template>
  <Portal to="showcase">
    <div class="px-3 md:px-6 bg-white md:mt-0" data-test-id="showcase-wrapper">
      <div class="max-w-1140 mx-auto">
        <div id="showcase-container" ref="showcase-container" :class="{ 'pb-3 md:pb-8 bg-white': showVideoPlaylist }"
          data-test-id="showcase-container">
          <div v-if="showVideoPlaylist" data-test-id="video-container"
            :style="{ height: useAspectRatio ? 'auto' : `${height}px` }" :class="{
              'bg-gray-1000': videoPlaylistPlayerLoaded,
              'bg-gray-100 flex justify-center items-center': !videoPlaylistPlayerLoaded,
               }" class="rounded-lg">
            <div class="rounded-lg" :class="{ 'aspect-16-9': useAspectRatio }" data-test-id="video-aspect-container">
              <EmbeddedVideoPlaylistPlayer v-if="videoPlaylistIsLoaded" v-show="videoPlaylistPlayerLoaded"
                :user-id="userId" :ratio-height-offset="ratioHeightOffset" data-test-id="embedded-video-playlist-player"
                @loaded="videoPlaylistPlayerLoaded = true" @resized="handleResized" />
            </div>

            <div v-if="!videoPlaylistPlayerLoaded && showVideoPlaylist"
              data-test-id="embedded-video-playlist-player-loading">
              <div class="w-full h-full flex items-center justify-center">
                <LoadingSpinner data-test-id="loading-spinner" />
              </div>
            </div>
          </div>
        </div>
        <AthleteProfileShowcaseEmptyState v-if="showEmptyState" :has-uploaded-video="hasUploadedVideo"
          data-test-id="empty-state" />
      </div>
    </div>
  </Portal>
</template>

<script>
import { defineComponent } from 'vue'
import EmbeddedVideoPlaylistPlayer from "@/components/VideoPlayers/VideoPlaylist/EmbeddedVideoPlaylistPlayer.vue";
import { Portal } from "portal-vue";
import { mapGetters } from "vuex";
import { LoadingSpinner } from '@sportsrecruits/playbook';
import AthleteProfileShowcaseEmptyState from "./AthleteProfileShowcaseEmptyState.vue";

export default defineComponent({
  name: "AthleteProfileShowcase",
  components: {
    AthleteProfileShowcaseEmptyState,
    EmbeddedVideoPlaylistPlayer,
    LoadingSpinner,
    Portal,
  },
  props: {
    userId: { required: true },
    showNavbar: { type: Boolean, default: false },
  },
  data() {
    return {
      videoPlaylistPlayerLoaded: false,
      useAspectRatio: true,
      height: 0,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };
  },
  computed: {
    ...mapGetters("videoLibrary", ["getVideoPlaylistIsLoaded", "getVideoPlaylistUrl"]),
    ...mapGetters("athleteProfile", ["isSameAthlete"]),
    ...mapGetters("userData", ["isLoggedOut", "isCollegeCoach", "isClubStaff"]),
    videoPlaylistIsLoaded() {
      return !!this.getVideoPlaylistIsLoaded;
    },
    showVideoPlaylist() {
      return this.videoPlaylistIsLoaded && !!this.getVideoPlaylistUrl;
    },
    showEmptyState() {
      return this.videoPlaylistIsLoaded && !this.getVideoPlaylistUrl && this.isSameAthlete;
    },
    isMobile() {
      return this.windowWidth < 816;
    },
    offset() {
      if (this.isLoggedOut) {
        return this.isMobile ? 186 : 122;
      }

      if (this.isCollegeCoach) {
        return this.isMobile ? 128 : 64;
      }

      if (this.isClubStaff) {
        return this.isMobile ? 268 : 140;
      }

      return this.isMobile ? 202 : 140;
    },
    ratioHeightOffset() {
      const containerMaxWidth = 1140
      const screenWidth = Math.min(this.windowWidth, containerMaxWidth);
      const margin = 16
      const screenHeight = this.windowHeight - this.offset - margin;
      const containerHeight = (screenWidth * 9) / 16;

      if (screenHeight >= containerHeight) {
        return 0;
      }
      return (screenHeight - containerHeight) | 0;
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResized(height) {
      this.height = height;
      this.useAspectRatio = false;
    },
    handleResize() {
      const newWidth = window.innerWidth
      if (newWidth !== this.windowWidth) {
        this.windowWidth = window.innerWidth;
      }
      const newHeight = document.documentElement.clientHeight
      if (newHeight !== this.windowHeight) {
        this.windowHeight = window.innerHeight;
      }
    },
  },
});
</script>
